import type { PlayerHistoryEntry } from '@/lib/api/usersApi';
import { COMPETITION_TYPE_LABELS as TYPE_LABELS } from '@shared';
import { Calendar, MapPin, Trophy, Users } from 'lucide-react';

const RANK_COLORS: Record<number, string> = {
  1: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  2: 'bg-slate-100 text-slate-600 border-slate-200',
  3: 'bg-orange-100 text-orange-700 border-orange-200',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export default function HistoryCard({ item }: { item: PlayerHistoryEntry }) {
  const rank = item.result?.rank ?? null;
  const rankCls = rank !== null && rank <= 3 ? RANK_COLORS[rank] : 'bg-slate-50 text-slate-500 border-slate-200';

  return (
    <div className='bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4'>
      <div className='flex-1 min-w-0'>
        <p className='font-bold text-slate-900 truncate'>{item.competition.name}</p>
        <div className='flex flex-wrap gap-3 mt-1 text-xs text-slate-500'>
          <span className='flex items-center gap-1'>
            <Calendar className='w-3.5 h-3.5' />
            {formatDate(item.competition.date)}
          </span>
          {(item.competition.venue ?? item.competition.city) && (
            <span className='flex items-center gap-1'>
              <MapPin className='w-3.5 h-3.5' />
              {item.competition.venue ?? item.competition.city}
            </span>
          )}
          <span className='flex items-center gap-1'>
            <Users className='w-3.5 h-3.5' />
            {TYPE_LABELS[item.competition.type] ?? item.competition.type}
          </span>
        </div>
        <p className='text-xs text-slate-400 mt-1'>
          Équipe : <span className='font-medium text-slate-600'>{item.teamName}</span>
          {item.role === 'CAPTAIN' && (
            <span className='ml-2 px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-md font-semibold'>Capitaine</span>
          )}
        </p>
      </div>
      <div className='shrink-0'>
        {rank !== null ? (
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-sm font-bold ${rankCls}`}>
            {rank <= 3 && <Trophy className='w-4 h-4' />}
            {rank === 1 ? '1ère place' : `${rank}e place`}
          </div>
        ) : (
          <span className='text-xs text-slate-400 italic'>Pas encore classé</span>
        )}
      </div>
    </div>
  );
}
